import React, { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";
import { Logo } from "./Logo";
import { useLang } from "@/i18n";
import { track } from "@/lib/api";

const DISMISS_KEY = "chela_install_dismissed";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

export default function InstallBanner() {
  const { t } = useLang();
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
      track("install_prompt_available", {});
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS never fires beforeinstallprompt — show manual hint after a short delay.
    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => setShow(true), 2500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    setIosHint(false);
    localStorage.setItem(DISMISS_KEY, "1");
    track("install_dismissed", {});
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice;
      track("install_choice", { outcome: choice?.outcome });
      setDeferred(null);
      setShow(false);
      if (choice?.outcome !== "accepted") localStorage.setItem(DISMISS_KEY, "1");
    } else if (isIOS()) {
      setIosHint(true);
    }
  };

  if (!show) return null;

  return (
    <div
      data-testid="install-banner"
      className="md:hidden fixed left-3 right-3 bottom-24 z-30 animate-rise"
    >
      <div className="bg-white border border-[#E5E0D8] rounded-2xl shadow-[0_14px_40px_rgba(0,0,0,0.18)] p-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#121316] flex items-center justify-center shrink-0">
            <Logo dark size="text-sm" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-[15px] text-[#121316] leading-tight">{t("install_title")}</div>
            <div className="text-xs text-[#5A5D65] mt-0.5">{t("install_sub")}</div>
          </div>
          <button data-testid="install-dismiss" onClick={dismiss} aria-label="Dismiss" className="h-8 w-8 rounded-full flex items-center justify-center text-[#5A5D65] hover:bg-[#F3EFEA]">
            <X size={16} />
          </button>
        </div>

        {iosHint ? (
          <div className="mt-3 text-xs text-[#3A3D45] bg-[#FFF1EC] border border-[#F6D9CD] rounded-xl px-3 py-2 flex items-center gap-2">
            <Share size={15} className="text-[#E05624] shrink-0" /> {t("install_ios")}
          </div>
        ) : (
          <button data-testid="install-btn" onClick={install} className="mt-3 w-full h-11 rounded-full btn-saffron font-bold text-sm inline-flex items-center justify-center gap-2">
            <Download size={16} /> {t("install_btn")}
          </button>
        )}
      </div>
    </div>
  );
}
